// Quotes a refund against the guest currency service. See README.md § Refund amount.
//
// initializeRefund does NOT just price the claim — it RESERVES the refund server-side and returns
// the id needed to finalize it, so it is a side effect, not a read. Hence an imperative request,
// fired once when the guest reaches D3 with a leaf reason, as iOS does in routeRefundableIssue.
// Quoting earlier would reserve refunds for claims the guest then abandons.
import { useCallback, useRef } from 'react'
import {
  GuestCurrencyInitializeRefundErrorCode,
  GuestCurrencyRefund,
  GuestCurrencyRefundReason,
  useInitializeRefundMutation
} from 'src/apollo/onlineOrdering'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'

// The reserved refund, as initializeRefund.gql selects it. `id` is the reservation handle ("must be
// used to finalize the refund" per the schema) — the real 'Refund GUID' Intercom attribute.
export type InitializedRefund = Pick<
  GuestCurrencyRefund,
  'id' | 'baseAmount' | 'bonusAmount'
>

export interface RefundQuote {
  // Non-null only on success. Never synthesised: showing an amount the service didn't reserve
  // would quote the guest a refund the workflow can't honour.
  refund: InitializedRefund | null
  // Set when the service declined in a typed way; null for success and for transport failure.
  errorCode: GuestCurrencyInitializeRefundErrorCode | null
  // Whether asking again could succeed. Only INTERNAL_ERROR qualifies: BAD_REQUEST and DENIED are
  // verdicts on this claim, and REFUND_ALREADY_ISSUED means the guest has one. Mirrors iOS.
  retryable: boolean
  loading: boolean
  // The mutation has landed — refund, decline, or failure. Separate from the three above because a
  // decline can arrive with a null `code`, indistinguishable on its own from never having asked.
  settled: boolean
  // The request never landed (offline, 5xx, aborted) — nothing to say about the claim itself.
  failed: boolean
}

export interface QuoteRefundInput {
  orderGuid: string
  rxGuid: string
  reason: GuestCurrencyRefundReason
  // CompletedOrderSelection guids for the items flagged in D2, scoping the reservation to them.
  // Absent or empty means the whole order, which is what a reason that skips D2 claims.
  selectionGuids?: readonly string[]
}

export interface UseInitializeRefundResult {
  quote: RefundQuote
  // Reserves a refund for `reason`. Never rejects — read `quote` instead. A call made while one is
  // already in flight is dropped: see the in-flight ref below.
  requestQuote: (input: QuoteRefundInput) => Promise<void>
  // Drops the current quote so a re-entry to D3 quotes afresh. Does NOT release the server-side
  // reservation: nothing in the schema does, and an unfinalized refund lapses on its own.
  resetQuote: () => void
}

export function useInitializeRefund(): UseInitializeRefundResult {
  const ooClient = useOOClient()
  const [initializeRefund, { data, loading, error, reset }] =
    useInitializeRefundMutation({ client: ooClient })

  // A ref, not `loading`: `loading` only turns true after a re-render, so two calls in the same tick
  // would both read false — and every call reserves a real refund that nothing ever releases.
  const inFlight = useRef(false)
  // Which request owns `inFlight`, so an abandoned one can't release it. See the `finally` below.
  const requestId = useRef(0)

  const requestQuote = useCallback(
    async ({ orderGuid, rxGuid, reason, selectionGuids }: QuoteRefundInput) => {
      // D3's retry button is why this exists: it re-renders in place, so unlike the D1/D2 handlers
      // a second click has somewhere to land, and a duplicate reservation is too costly to leave
      // to React's flush timing.
      if (inFlight.current) return
      inFlight.current = true
      const token = ++requestId.current
      try {
        await initializeRefund({
          variables: {
            input: {
              orderId: orderGuid,
              restaurantId: rxGuid,
              reason,
              // Omitted rather than sent empty: the schema reads an absent `selectionIds` as
              // "the entire order", while `[]` claims the guest flagged nothing.
              ...(selectionGuids?.length
                ? { selectionIds: [...selectionGuids] }
                : {})
            }
          }
        })
      } catch {
        // Apollo rejects the promise AND records `error`; swallowed so callers read `quote.failed`.
      } finally {
        // Only the request still owning the flag may release it: an abandoned one (reason A reset
        // away, then B quoted) finishes later and would reopen the guard while B is outstanding.
        if (requestId.current === token) inFlight.current = false
      }
    },
    [initializeRefund]
  )

  // Cleared alongside the quote: a guest going back to a different reason must be able to quote it
  // even with the abandoned request outstanding, or D3 strands on a spinner. That one lapses.
  const resetQuote = useCallback(() => {
    inFlight.current = false
    reset()
  }, [reset])

  const result = data?.guestCurrency?.initializeRefund
  // Narrowed on __typename, added implicitly by addTypenameSelectionDocumentTransform (codegen.ts).
  const refund = result?.__typename === 'GuestCurrencyRefund' ? result : null
  const errorCode =
    result?.__typename === 'GuestCurrencyInitializeRefundError'
      ? // `code` is nullable, so normalise to null: "declined, cause unknown".
        result.code ?? null
      : null

  return {
    quote: {
      refund,
      errorCode,
      retryable:
        errorCode === GuestCurrencyInitializeRefundErrorCode.InternalError,
      loading,
      // Keyed off the response, not `result`: a response carrying neither union branch has still
      // landed, and reporting it as pending would hang D3 on a request that already came back.
      settled: Boolean(data) || Boolean(error),
      failed: Boolean(error)
    },
    requestQuote,
    resetQuote
  }
}
